import { useState } from 'react';
import { pagesPubliquesApi } from '../../api/resources';
import { C } from '../../theme/palette';

/**
 * « Si il transmet mon offre à quelqu'un, je veux savoir à qui. » Deux gestes
 * sur la page du client : une personne nouvelle peut donner son nom, et le
 * client peut transmettre la pièce à une autre personne, par e-mail. Le patron
 * lit les noms dans le suivi de la pièce et dans la cloche.
 */

const carte = {
  background: '#fff',
  border: '1px solid ' + C.border,
  borderRadius: 14,
  padding: 18,
  marginBottom: 14,
};

const champ = {
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  marginTop: 6,
  padding: '12px 14px',
  fontSize: 15,
  border: '1px solid ' + C.border,
  borderRadius: 10,
};

const libelle = { display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 10 };

const bouton = (fond, actif = true) => ({
  flex: 1,
  textAlign: 'center',
  background: actif ? fond : C.muted,
  color: '#fff',
  fontWeight: 800,
  fontSize: 15,
  borderRadius: 12,
  padding: '13px 14px',
  border: 'none',
  cursor: actif ? 'pointer' : 'default',
  fontFamily: 'inherit',
});

const boutonClair = {
  flex: 1,
  textAlign: 'center',
  background: '#fff',
  color: C.ink,
  fontWeight: 700,
  fontSize: 15,
  borderRadius: 12,
  padding: '13px 14px',
  border: '1px solid ' + C.border,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

/** La personne a déjà répondu « Non merci » sur cet appareil. */
const cleRefus = (jeton) => 'st_nom_' + jeton;

/**
 * Lit le refus gardé sur cet appareil.
 *
 * @param {string} jeton jeton porté par le lien
 * @returns {boolean} vrai si la personne a dit « Non merci »
 */
function aRefuseDeSeNommer(jeton) {
  try {
    return window.localStorage.getItem(cleRefus(jeton)) === 'non';
  } catch {
    return false;
  }
}

/**
 * Une personne nouvelle ouvre le lien : elle peut dire qui elle est. C'est
 * facultatif, et ne se redemande pas après « Non merci » (rien ne s'affiche).
 *
 * @param {object} props propriétés du composant
 * @param {string} props.jeton jeton porté par le lien
 * @param {string} [props.client] nom du client de la pièce, proposé en « C'est moi »
 * @param {(nom: ?string) => void} props.onFin reçoit le nom donné, ou rien après « Non merci »
 * @returns {import('react').ReactElement} la carte
 */
export function DemandeNom({ jeton, client, onFin }) {
  const [nom, setNom] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [refuse] = useState(() => aRefuseDeSeNommer(jeton));
  const pret = nom.trim() !== '' && !envoi;
  if (refuse) return null;

  const envoie = async (donne = nom.trim()) => {
    setEnvoi(true);
    setErreur('');
    try {
      const r = await pagesPubliquesApi.nomme(jeton, donne);
      onFin(r?.nom || donne);
    } catch (e) {
      setErreur(e?.message || 'Ce nom n’a pas pu être enregistré.');
      setEnvoi(false);
    }
  };
  const nonMerci = () => {
    try {
      window.localStorage.setItem(cleRefus(jeton), 'non');
    } catch {
      /* sans mémoire : la question reviendra, rien de plus */
    }
    onFin(null);
  };

  return (
    <section style={{ ...carte, border: '1px solid ' + C.navy }} data-personne="demande-nom">
      <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 6 }}>
        Qui consulte ce document ?
      </div>
      <p style={{ fontSize: 14, color: C.grey, margin: '0 0 12px', lineHeight: 1.5 }}>
        Indiquez votre nom : l’entreprise saura à qui elle s’adresse. C’est facultatif.
      </p>
      {/* Le client lui-même, dans un autre navigateur (Gmail puis Chrome) :
          sans ce bouton, l'entreprise ne pouvait pas savoir que c'était lui. */}
      {client && (
        <button
          type="button"
          disabled={envoi}
          onClick={() => envoie(client)}
          style={{ ...bouton(C.navy, !envoi), width: '100%', marginBottom: 12 }}
        >
          C’est moi — <span translate="no">{client}</span>
        </button>
      )}
      <label style={libelle}>
        Votre nom
        <input
          value={nom}
          onChange={(e) => setNom(e.target.value)}
          placeholder="Prénom et nom"
          maxLength={80}
          autoComplete="name"
          style={champ}
        />
      </label>
      {erreur && (
        <div role="alert" style={{ color: C.red, fontSize: 13, marginBottom: 10 }}>
          {erreur}
        </div>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <button type="button" onClick={nonMerci} style={boutonClair}>
          Non merci
        </button>
        <button
          type="button"
          disabled={!pret}
          onClick={() => envoie()}
          style={bouton(C.navy, pret)}
        >
          {envoi ? 'Envoi…' : 'Envoyer'}
        </button>
      </div>
    </section>
  );
}

/**
 * Transmettre la pièce à une autre personne : son nom et son e-mail. SwissTech
 * lui envoie le lien ; l'entreprise voit à qui.
 *
 * @param {object} props propriétés du composant
 * @param {string} props.jeton jeton porté par le lien
 * @param {string} [props.votreNom] nom de la personne qui transmet, s'il est connu
 * @returns {import('react').ReactElement} le bouton, puis le formulaire
 */
export function Transmettre({ jeton, votreNom }) {
  const [ouvert, setOuvert] = useState(false);
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [deLaPart, setDeLaPart] = useState(votreNom || '');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [envoyes, setEnvoyes] = useState([]);

  const pret = nom.trim() !== '' && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) && !envoi;
  const envoie = async () => {
    setEnvoi(true);
    setErreur('');
    try {
      const r = await pagesPubliquesApi.transmet(jeton, {
        nom: nom.trim(),
        email: email.trim(),
        deLaPart: deLaPart.trim() || undefined,
      });
      setEnvoyes((p) => [...p, r?.nom ? r : { nom: nom.trim(), email: email.trim() }]);
      setNom('');
      setEmail('');
      setOuvert(false);
    } catch (e) {
      setErreur(e?.message || 'L’envoi n’a pas abouti. Vérifiez le nom et l’adresse e-mail.');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div style={{ marginBottom: 14 }}>
      {envoyes.map((t, i) => (
        <div
          key={i}
          role="status"
          data-personne="transmis"
          style={{
            background: C.greenL,
            border: '1px solid ' + C.green,
            color: C.green,
            borderRadius: 12,
            padding: '11px 14px',
            fontSize: 14,
            fontWeight: 700,
            marginBottom: 10,
            overflowWrap: 'anywhere',
          }}
        >
          ✓ Envoyé à {t.nom} ({t.email})
        </div>
      ))}
      {!ouvert ? (
        <button
          type="button"
          onClick={() => setOuvert(true)}
          style={{ ...boutonClair, display: 'block', width: '100%', fontWeight: 800 }}
        >
          📤 Transmettre à une autre personne
        </button>
      ) : (
        <section
          style={{ ...carte, marginBottom: 0 }}
          aria-label="Transmettre à une autre personne"
        >
          <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 6 }}>
            📤 Transmettre à une autre personne
          </div>
          <p style={{ fontSize: 14, color: C.grey, margin: '0 0 12px', lineHeight: 1.5 }}>
            Elle recevra ce document par e-mail.
          </p>
          <label style={libelle}>
            Nom de la personne
            <input
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              placeholder="Prénom et nom"
              maxLength={80}
              style={champ}
            />
          </label>
          <label style={libelle}>
            Son adresse e-mail
            <input
              type="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nom@exemple.ch"
              maxLength={200}
              autoCapitalize="none"
              style={champ}
            />
          </label>
          <label style={libelle}>
            Votre nom
            <input
              value={deLaPart}
              onChange={(e) => setDeLaPart(e.target.value)}
              placeholder="Prénom et nom"
              maxLength={80}
              autoComplete="name"
              style={champ}
            />
          </label>
          {erreur && (
            <div role="alert" style={{ color: C.red, fontSize: 13, marginBottom: 10 }}>
              {erreur}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={() => {
                setOuvert(false);
                setErreur('');
              }}
              style={boutonClair}
            >
              Annuler
            </button>
            <button type="button" disabled={!pret} onClick={envoie} style={bouton(C.navy, pret)}>
              {envoi ? 'Envoi…' : 'Envoyer'}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
